/**
 * #345 — the "Open orders — this game" panel's orchestration, extracted from
 * `GameOpenOrders` so the component is presentation only.
 *
 * What lives here: the store reads, the Polymarket poll, the memos that turn
 * live order arrays into rows, and the five commands. What deliberately does
 * NOT live here: the row shaping itself, which sits in `gameOpenOrderRows` so
 * it can be tested without a store mock in sight.
 *
 * Four properties this file is responsible for preserving exactly:
 *
 *   1. The DISPLAY lists and the DESTRUCTIVE scope stay separately derived.
 *      `kalshiRows` carries a moneyline-ticker fallback so a row still draws
 *      before its market frame streams in; `selectExactGameOrders` refuses that
 *      fallback because a ticker match is not proof of which game an order
 *      belongs to. Collapsing them into one selector would let an unidentified
 *      order receive a DELETE. They are not duplicate logic (#272).
 *   2. `openGameCancel` freezes the target set at the CLICK. The snapshot is
 *      the modal state — a boolean would re-read the live arrays at confirm
 *      time and send a set the trader never saw.
 *   3. `runBulkCancel`'s re-entry guard is a ref, not state. React state lands
 *      a tick late and a double-click on confirm lands in the same tick.
 *   4. The per-row pending fence is read from the STORE, never mirrored into
 *      local state. A successful DELETE holds the row disabled until an
 *      authoritative snapshot drops the ID; a local flag cleared in a `finally`
 *      is the #241 defect.
 *
 * `isEmpty` reports the render-nothing case rather than the hook returning
 * early — hooks cannot branch, and the caller needs the flag anyway.
 *
 * #1016 — the trader's own Polymarket US orders join the rows and the game
 * sweep. They are read (`useGameUsOpenOrders`), not held in a store, so the
 * confirmation freezes the US set that is established at the click beside the
 * K/P snapshot, and says so when US is still loading or unreadable. The two
 * legs run independently: a slow US read or cancel never delays a K/P DELETE.
 */
import { useCallback, useMemo, useRef, useState } from 'react';
import { useAccountStore } from '../../stores/accountStore';
import { usePinnedKalshiOrderIds } from '../../hooks/usePinnedKalshiOrderIds';
import { useMarketStore } from '../../stores/marketStore';
import { useDisplaySettings } from '../../stores/displaySettings';
import { useOscarAccess } from '../../hooks/useOscarAccess';
import {
  usePolymarketRelayOrders,
  invalidatePolyOrdersShare,
} from '@/hooks/usePolymarketRelayOrders';
import { cancelPolyOrderViaRelay } from '@/lib/polymarket/relay';
import { describePolyOrderCached } from '@/lib/polymarket/describeOrder';
import { isKalshiCancelPending } from '../../lib/trading/kalshiCancelLifecycle';
import {
  describeAllPinned,
  freezeCancelSnapshot,
  selectExactGameOrders,
  type FrozenCancelSnapshot,
  type GameOrderBlocker,
} from '@/lib/trading/gameOrderSelection';
import {
  executeBulkOrderCancel,
  type BulkCancelReport,
} from '@/lib/trading/bulkOrderCancel';
import {
  kalshiOrderMatchesPanel,
  polyOrderMatchesPanel,
  projectKalshiOpenOrderRow,
  projectPolyOpenOrderRow,
  type KalshiOpenOrderRow,
  type PolyOpenOrderRow,
} from './gameOpenOrderRows';
import type { OscarReleasePrefill } from './oscar';
import type { GameRow } from './types';
import { createUsBrowserPortfolioClient } from '@/lib/polymarketUs/browserPortfolioClient';
import type { UsGameBlocker, UsGameNotice } from '@/lib/polymarketUs/gameOpenOrders';
import { runUsBulkCancel, type UsBulkInventory, type UsBulkResult } from '../PositionTracker/usBulkCancelModel';
import { readUsSessionKey } from '../PositionTracker/usTrackerFeed';
import { projectUsOpenOrderRow, type UsOpenOrderRow } from './gameOpenOrderRows';
import type { GameUsOpenOrders } from './useGameUsOpenOrders';

const NO_US_BLOCKERS: readonly UsGameBlocker[] = [];
const NO_US_NOTICES: readonly UsGameNotice[] = [];

/** A cancel whose confirmation the venue never sent. Not a failure — the
 *  DELETE succeeded — so the row keeps its control and shows this instead. */
export interface TimedOutCancelNotice {
  orderId: string;
  message: string;
}

/** The US part of a game confirmation, frozen at the click. */
export type UsGameCancelPart =
  /** No US for this game or login. */
  | { phase: 'off' }
  /** US may hold orders for this game but none could be frozen: named as left out. */
  | { phase: 'omitted'; loading: boolean; code: string | null }
  | { phase: 'frozen'; inventory: UsBulkInventory; unresolved: readonly UsGameNotice[] };

/** What "Cancel this game" will send: the K/P snapshot and the US part, frozen
 *  together at the click, for one login and one game. */
export interface GameCancelConfirmation {
  kp: FrozenCancelSnapshot;
  us: UsGameCancelPart;
  /** Login + game the confirmation was frozen under. */
  identity: string;
  sessionKey: string;
}

/** A finished US leg. Kept with the login + game it ran under. */
export interface UsGameCancelReport {
  results: readonly UsBulkResult[];
  /** Why US sent nothing, when it was left out. */
  note: string | null;
}

/** The per-row cancel awaiting the trader's approval. */
export interface PendingRowCancel {
  orderId: string;
  label: string;
}

export interface GameOpenOrdersModel {
  /** No rows, no blockers, no report — the panel draws no section at all. */
  isEmpty: boolean;

  // ── Rows ──
  kalshiRows: readonly KalshiOpenOrderRow[];
  polyRows: readonly PolyOpenOrderRow[];
  /** #1016: the trader's own US orders for this exact game, view-only. */
  usRows: readonly UsOpenOrderRow[];
  /** US read state for the section: still reading, or unreadable with a code. */
  /** `stale`: the latest re-read failed (`code`); the rows shown are the read
   *  taken at `readAtMs`, left out of the game sweep. */
  usStatus: { loading: boolean; error: string | null; stale: { code: string; readAtMs: number } | null };
  /** US orders that could not be tied to any game; never claimed for this one. */
  usUnresolved: readonly UsGameNotice[];
  refreshUs: () => void;
  /** Whether this order's cancel controls are fenced. A timed-out cancel is
   *  deliberately NOT pending — the trader gets the control back. */
  isCancelPending: (orderId: string) => boolean;
  timedOutNotices: readonly TimedOutCancelNotice[];

  // ── Per-row cancel ──
  cancelError: string | null;
  pendingRowCancel: PendingRowCancel | null;
  /** Opens the confirmation, or cancels immediately when the trader has turned
   *  confirm-before-cancel off. */
  requestRowCancel: (orderId: string, label: string) => void;
  confirmRowCancel: () => void;
  dismissRowCancel: () => void;

  // ── Game-wide cancel ──
  blockers: readonly GameOrderBlocker[];
  /** US orders tied to this matchup but not to exactly this game. */
  usBlockers: readonly UsGameBlocker[];
  canBulkCancel: boolean;
  bulkRunning: boolean;
  bulkReport: BulkCancelReport | null;
  usReport: UsGameCancelReport | null;
  /** The frozen set under confirmation. Non-null IS the modal being open. */
  pendingGameCancel: GameCancelConfirmation | null;
  /** Why the action is enabled or not, in the trader's words. */
  bulkCancelTitle: string;
  openGameCancel: () => void;
  confirmGameCancel: () => void;
  dismissGameCancel: () => void;
  retryFailedCancels: () => void;
}

export interface GameOpenOrdersModelInput {
  game: GameRow;
  /** Present only when the panel can accept an armed release. Folded into the
   *  row projection's gate together with the trader's oscar access. */
  onOscarRelease?: (prefill: OscarReleasePrefill) => void;
  /** #1016/#1233: the game's US read (`useGameUsOpenOrders`), owned by the
   *  panel so the held snapshot shares it. */
  us: GameUsOpenOrders;
}

export function useGameOpenOrdersModel({
  game,
  onOscarRelease,
  us,
}: GameOpenOrdersModelInput): GameOpenOrdersModel {
  const openOrders = useAccountStore((s) => s.openOrders);
  const cancelOrder = useAccountStore((s) => s.cancelOrder);
  const cancelOrders = useAccountStore((s) => s.cancelOrders);
  const refreshOrders = useAccountStore((s) => s.refreshOrders);
  const oscarAccess = useOscarAccess();
  // Honor the app-wide confirm-before-cancel setting (default ON) like every other
  // cancel surface — an inline card cancel must not bypass the trader's safety gate.
  const confirmOrderCancel = useDisplaySettings((s) => s.confirmOrderCancel);
  // Re-run the canonical-key match as frames arrive — a sibling market that
  // streams in after mount (spread/total tab not yet warmed) then matches.
  const marketVersion = useMarketStore((s) => s.version);
  // Poll Polymarket open orders only while this card is mounted (panel open).
  // The hook is inert in personal-key / no-cred modes (returns []), so the poly
  // section simply doesn't render then. Exactly ONE instance — the panel adds no
  // second poll.
  const { orders: polyOrders, refresh: refreshPolyOrders } = usePolymarketRelayOrders();
  // #241: the pending-cancel fence is shared store state, not local — a
  // successful DELETE keeps the row disabled until an authoritative snapshot
  // drops the ID, and the same fence has to hold on every mounted surface.
  const kalshiCancelStates = useAccountStore((s) => s.kalshiCancelStates);

  const [cancelError, setCancelError] = useState<string | null>(null);
  const [pendingRowCancel, setPendingRowCancel] = useState<PendingRowCancel | null>(null);
  // The snapshot IS the modal state — storing a boolean here is the bug this
  // replaces: a boolean modal would re-read the live arrays at confirm time and
  // send a set the trader never saw.
  const [pendingState, setPendingGameCancel] = useState<GameCancelConfirmation | null>(null);
  const [bulkReport, setBulkReport] = useState<BulkCancelReport | null>(null);
  const [usReportState, setUsReport] = useState<(UsGameCancelReport & { identity: string }) | null>(null);
  const [bulkRunning, setBulkRunning] = useState(false);
  // Synchronous re-entry guard. React state lands a tick late; a double-click on
  // the confirm button lands in the same tick.
  const runningRef = useRef(false);

  const awayTicker = game.kalshi?.away?.marketTicker;
  const homeTicker = game.kalshi?.home?.marketTicker;
  const moneylineTickers = useMemo(
    () => new Set([awayTicker, homeTicker].filter((t): t is string => !!t)),
    [awayTicker, homeTicker],
  );

  // Kalshi: any resting order whose market resolves to this game's canonical
  // key (moneyline + spread + total + period), with the moneyline-ticker Set
  // as the fallback when the frame isn't loaded so canonical resolution misses.
  const orders = useMemo(() => {
    const store = useMarketStore.getState();
    return openOrders.filter((o) => {
      // Fail-closed resolution (DNL-001): an ambiguous doubleheader frame
      // resolves to NO game rather than being promoted to gameNo=1, which would
      // seat another game's resting order on this card. '' simply doesn't
      // match; the moneyline-ticker fallback below still covers a missing frame.
      const canon = store.getGameKeyV2ForTicker(o.ticker) ?? '';
      return kalshiOrderMatchesPanel(canon, o.ticker, game.key, moneylineTickers);
    });
    // marketVersion drives re-filtering as frames stream in.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openOrders, moneylineTickers, game.key, marketVersion]);

  // Labels, the side-native price and the blocker's release prefill. Split from
  // the filter above because it depends on oscar access rather than on which
  // markets have streamed in.
  const kalshiRows: KalshiOpenOrderRow[] = useMemo(
    () =>
      orders.map((ord) =>
        projectKalshiOpenOrderRow(ord, { releaseEnabled: oscarAccess && !!onOscarRelease }),
      ),
    [orders, oscarAccess, onOscarRelease],
  );

  // Polymarket: asset_id → ticker → market → canonical key === orderScopeGameKey(game.key).
  // Display-only; labels come from the shared poly-order describer.
  const polyRows = useMemo(() => {
    const store = useMarketStore.getState();
    const matched: PolyOpenOrderRow[] = [];
    for (const po of polyOrders) {
      const assetId = typeof po.asset_id === 'string' ? po.asset_id : '';
      if (!assetId) continue;
      const ticker = store.getTickerByTokenId(assetId);
      const mkt = ticker ? store.getMarket(ticker) : null;
      if (!mkt) continue;
      // Same fail-closed resolution as the Kalshi branch (DNL-001). Poly is the
      // venue that actually produces ambiguous DH frames — its slug can only
      // address the matchup-date, not the game.
      const canon = store.getGameKeyV2ForTicker(ticker!) ?? '';
      if (!polyOrderMatchesPanel(canon, game.key)) continue;
      matched.push(
        projectPolyOpenOrderRow(po, {
          assetId,
          market: mkt,
          description: describePolyOrderCached(assetId),
        }),
      );
    }
    return matched;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [polyOrders, game.key, marketVersion]);

  // ── #272: the DESTRUCTIVE scope, derived separately from the display lists ──
  // Deliberately not reused from `orders`/`polyRows` above: those carry the
  // display-only ticker fallback, which is fine for drawing a row and is not
  // proof of identity for a DELETE. The selector re-derives from the same live
  // own-scoped arrays under the exact-key rule and reports what must block.
  // The trader's opt-in: identify a game from its Kalshi ticker when the feed
  // has not tied the market to a game yet (off = the exact feed key only).
  const tickerFallback = useDisplaySettings((s) => s.gameCancelTickerFallback);
  const cancelSelection = useMemo(
    () => selectExactGameOrders({ game, kalshiOrders: openOrders, polyOrders, tickerFallback }),
    // marketVersion re-runs the resolution as frames stream in, exactly like the
    // display memos above.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [game, openOrders, polyOrders, marketVersion, tickerFallback],
  );

  // #943 — pinned Kalshi orders are skipped by this sweep, like Cancel All.
  const pinnedKalshiIds = usePinnedKalshiOrderIds();
  const pinnedTargetCount = cancelSelection.targets.filter(
    (t) => t.venue === 'kalshi' && pinnedKalshiIds.has(t.orderId),
  ).length;
  const unpinnedTargetCount = cancelSelection.targets.length - pinnedTargetCount;

  const blockers = cancelSelection.blockers;

  // ── #1016: the trader's own US orders for this game ──
  // #1233: a stale read (its latest re-read failed) still draws its rows, but
  // the sweep treats US as unreadable: nothing is sent from it (`usCurrent` is
  // only a read that answered). Its blockers still hold: nothing about those
  // orders changed, so a network blip must not enable the sweep.
  const usShown = us.orders.phase === 'ready' ? us.orders : null;
  const usCurrent = usShown && usShown.staleCode === null ? usShown : null;
  const usRows = useMemo(
    () => (usShown ? usShown.selection.rows.map((r) => projectUsOpenOrderRow(r, usShown.readAtMs)) : []),
    [usShown],
  );
  const usBlockers = usShown?.selection.blockers ?? NO_US_BLOCKERS;
  const usUnresolved = usShown?.selection.unresolved ?? NO_US_NOTICES;
  const usTargetCount = usCurrent?.inventory.targets.length ?? 0;
  const usError = us.orders.phase === 'error' ? us.orders.code : null;
  const usStale = usShown?.staleCode ? { code: usShown.staleCode, readAtMs: usShown.readAtMs } : null;
  // A confirmation or report belongs to one login and one game; a change of
  // either discards it (and an open confirmation then sends nothing).
  const identity = `${us.sessionKey}|${cancelSelection.gameKey}`;
  // Discard during render, before the new game can commit. Merely hiding a
  // mismatched snapshot lets A -> B -> A resurrect A's old confirmation.
  if (pendingState && pendingState.identity !== identity) setPendingGameCancel(null);
  if (usReportState && usReportState.identity !== identity) setUsReport(null);
  const pendingGameCancel = pendingState?.identity === identity ? pendingState : null;
  const usReport = usReportState?.identity === identity ? usReportState : null;

  const canBulkCancel =
    blockers.length === 0 &&
    usBlockers.length === 0 &&
    (unpinnedTargetCount > 0 || usTargetCount > 0) &&
    !bulkRunning;

  // Timed-out fences for orders visible on THIS card. Derived from shared state
  // rather than copied into local state, so a retry or an authoritative removal
  // clears the notice without a second code path.
  const timedOutNotices = useMemo(
    () =>
      orders.flatMap((ord) => {
        const state = kalshiCancelStates[ord.orderId];
        return state?.phase === 'timed-out'
          ? [{ orderId: ord.orderId, message: state.message ?? '' }]
          : [];
      }),
    [orders, kalshiCancelStates],
  );

  const isCancelPending = useCallback(
    (orderId: string) => isKalshiCancelPending(kalshiCancelStates[orderId]),
    [kalshiCancelStates],
  );

  const executeRowCancel = useCallback(
    async (orderId: string) => {
      setCancelError(null);
      try {
        // The store owns the pending state now — no local flag to clear in a
        // `finally`, which is what let the row re-enable before removal (#241).
        await cancelOrder(orderId);
      } catch (err) {
        // A failed cancel must NOT look like success on a live-game card — surface it.
        setCancelError(
          err instanceof Error && err.message
            ? err.message
            : 'Cancel failed. Check open orders and try again.',
        );
      }
    },
    [cancelOrder],
  );

  const requestRowCancel = useCallback(
    (orderId: string, label: string) => {
      setCancelError(null);
      if (confirmOrderCancel) {
        setPendingRowCancel({ orderId, label });
        return;
      }
      void executeRowCancel(orderId);
    },
    [confirmOrderCancel, executeRowCancel],
  );

  const confirmRowCancel = useCallback(() => {
    // Clear the target BEFORE firing: the store's duplicate guard is the
    // final safety net, but the modal shouldn't be able to re-submit.
    const target = pendingRowCancel;
    setPendingRowCancel(null);
    if (target) void executeRowCancel(target.orderId);
  }, [pendingRowCancel, executeRowCancel]);

  const dismissRowCancel = useCallback(() => setPendingRowCancel(null), []);

  const openGameCancel = useCallback(() => {
    if (!canBulkCancel) return;
    setBulkReport(null);
    setUsReport(null);
    // US: only what is established now. A read still running or failed is
    // named as left out; its later completion cannot add to this confirmation.
    const orders = us.orders;
    const usPart: UsGameCancelPart =
      orders.phase === 'ready' && orders.staleCode !== null
        ? { phase: 'omitted', loading: false, code: orders.staleCode }
        : orders.phase === 'ready'
        ? orders.inventory.targets.length || orders.inventory.excluded.length || orders.selection.unresolved.length
          ? { phase: 'frozen', inventory: orders.inventory, unresolved: orders.selection.unresolved }
          : { phase: 'off' }
        : orders.phase === 'loading'
          ? { phase: 'omitted', loading: true, code: null }
          : orders.phase === 'error'
            ? { phase: 'omitted', loading: false, code: orders.code }
            : { phase: 'off' };
    // Frozen HERE, from the click — not read again when confirm fires.
    setPendingGameCancel({
      kp: freezeCancelSnapshot({
        scope: 'game',
        label: cancelSelection.gameLabel,
        targets: cancelSelection.targets,
        pinnedKalshiOrderIds: pinnedKalshiIds,
      }),
      us: usPart,
      identity,
      sessionKey: us.sessionKey,
    });
  }, [canBulkCancel, cancelSelection, pinnedKalshiIds, us.orders, us.sessionKey, identity]);

  const refreshUs = us.refresh;
  const runBulkCancel = useCallback(
    async (confirmation: GameCancelConfirmation) => {
      if (runningRef.current) return;
      runningRef.current = true;
      setBulkRunning(true);
      const { kp, us: usPart } = confirmation;
      const usTargets = usPart.phase === 'frozen' ? usPart.inventory.targets : [];
      // Both legs start now; neither waits for the other.
      const kpLeg = kp.targets.length > 0
        ? executeBulkOrderCancel(kp, {
            // refreshAfter:false — this executor owns the single per-venue refresh.
            cancelKalshiOrders: (ids) => cancelOrders(ids, { refreshAfter: false }),
            requestPolymarketCancel: cancelPolyOrderViaRelay,
            refreshKalshi: () => refreshOrders(),
            refreshPolymarket: async () => {
              invalidatePolyOrdersShare();
              await refreshPolyOrders(true);
            },
          })
        : Promise.resolve(null);
      // A fresh client; a login or org change stops the unsent rest.
      const usLeg = usTargets.length > 0
        ? runUsBulkCancel({ portfolio: createUsBrowserPortfolioClient() }, usTargets,
            () => readUsSessionKey() === confirmation.sessionKey)
        : Promise.resolve(null);
      try {
        const [kpResult, usResult] = await Promise.allSettled([kpLeg, usLeg]);
        if (kpResult.status === 'fulfilled') {
          if (kpResult.value) setBulkReport(kpResult.value);
        } else {
          setCancelError(kpResult.reason instanceof Error && kpResult.reason.message
            ? kpResult.reason.message : 'Cancel failed. Check open orders and try again.');
        }
        if (usPart.phase === 'omitted') {
          setUsReport({ identity: confirmation.identity, results: [], note: usPart.loading
            ? 'Polymarket US was not included: its orders were still being read when you opened this. Reopen Cancel this game to include them.'
            : `Polymarket US was not included: its orders could not be read (${usPart.code}).` });
        } else if (usTargets.length > 0) {
          setUsReport(usResult.status === 'fulfilled'
            ? { identity: confirmation.identity, results: usResult.value ?? [], note: null }
            : { identity: confirmation.identity, results: [], note: 'Polymarket US cancels could not be completed. Check status in Position Tracker before cancelling again.' });
        }
      } finally {
        runningRef.current = false;
        setBulkRunning(false);
        // The US rows are read, not pushed: read them again after a sweep.
        if (usTargets.length > 0) refreshUs();
      }
    },
    [cancelOrders, refreshOrders, refreshPolyOrders, refreshUs],
  );

  const confirmGameCancel = useCallback(() => {
    const confirmation = pendingGameCancel;
    // Closed BEFORE the first await so the confirm button cannot be hit twice.
    setPendingGameCancel(null);
    // The login can change before React re-renders; a stale confirmation sends nothing.
    if (!confirmation || readUsSessionKey() !== confirmation.sessionKey) return;
    void runBulkCancel(confirmation);
  }, [pendingGameCancel, runBulkCancel]);

  const dismissGameCancel = useCallback(() => setPendingGameCancel(null), []);

  const retryFailedCancels = useCallback(() => {
    const failed = bulkReport?.failed ?? [];
    if (failed.length === 0 || bulkRunning) return;
    // Failures ONLY. A submitted ID resent here would re-DELETE a cancellation
    // that already worked and fail misleadingly (#241's original defect).
    const snapshot = freezeCancelSnapshot({
      scope: 'game',
      label: cancelSelection.gameLabel,
      targets: failed.map((row) => row.target),
      pinnedKalshiOrderIds: pinnedKalshiIds,
    });
    // Every failure has been pinned since: nothing to confirm (#943 review).
    if (snapshot.targets.length === 0) {
      setCancelError(describeAllPinned(snapshot));
      return;
    }
    // K/P failures only: a US cancel is never repeated from here.
    setPendingGameCancel({ kp: snapshot, us: { phase: 'off' }, identity, sessionKey: us.sessionKey });
  }, [bulkReport, bulkRunning, cancelSelection, pinnedKalshiIds, identity, us.sessionKey]);

  const bulkCancelTitle =
    blockers.length > 0 || usBlockers.length > 0
      ? 'Cannot safely cancel this game — see the reason below'
      : cancelSelection.targets.length === 0 && usTargetCount === 0
        ? us.orders.phase === 'loading'
          ? 'Reading your Polymarket US orders…'
          : usStale
            ? 'No confirmed open orders for this game — Polymarket US orders unavailable, showing the last reading'
            : 'No confirmed open orders for this game'
        : unpinnedTargetCount === 0 && usTargetCount === 0
          ? 'Every open order for this game is pinned — unpin to include'
          : pinnedTargetCount > 0
            ? `Cancel every confirmed open order for this game — ${pinnedTargetCount} pinned skipped`
            : 'Cancel every confirmed open order for this game';

  return {
    // A finished bulk report keeps the section mounted after the last row has
    // gone: the outcome of a destructive action must outlive its targets.
    isEmpty:
      kalshiRows.length === 0 &&
      polyRows.length === 0 &&
      blockers.length === 0 &&
      bulkReport === null &&
      usRows.length === 0 &&
      usBlockers.length === 0 &&
      usUnresolved.length === 0 &&
      usError === null &&
      usStale === null &&
      usReport === null,
    kalshiRows,
    polyRows,
    usRows,
    usStatus: { loading: us.orders.phase === 'loading', error: usError, stale: usStale },
    usUnresolved,
    refreshUs,
    isCancelPending,
    timedOutNotices,
    cancelError,
    pendingRowCancel,
    requestRowCancel,
    confirmRowCancel,
    dismissRowCancel,
    blockers,
    usBlockers,
    canBulkCancel,
    bulkRunning,
    bulkReport,
    usReport: usReport ? { results: usReport.results, note: usReport.note } : null,
    pendingGameCancel,
    bulkCancelTitle,
    openGameCancel,
    confirmGameCancel,
    dismissGameCancel,
    retryFailedCancels,
  };
}
